function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import React from "react";
import { withRouter } from "react-router";
import { Link } from "react-router-dom";
import { Card, Empty, Pending } from "../../components/analytics-kit";
import { Loader } from "../../components/loaders";
import Error from "../../components/error";
import ServiceFactory from "../../service";
import { num, usd } from "../sales/territory-common";
import AudienceTrendChart, { fetchTrends, rangeWindow } from "./trend-chart";
import { getGlobalRange, setGlobalRange, subscribePeriod, unsubscribePeriod } from "../../functions/period-store";
import AudiencesShell, { SUBTITLES } from "./shell";
import { Button } from "@progress/kendo-react-buttons";
import ListEditor from "./list-editor";
import { toast, overlapDetail, fromDifference, getListCards } from "./api";
import { InlineConfirm, ErrorLine } from "./ui";

/*
 * Audiences: the "Lists" tab (redesign 2026-10-09, PlunkDocs mockups/audiences-redesign-2026-10-09.html board 1).
 *
 * Joins four live sources client-side by audience id (name as a fallback for stats):
 *   GET /v1/audience                     rows (id/name/status/type/provider/kind/description/total)
 *   GET /v1/audience/cards               per list: members, withEmail, 8-point member trend (oldest first,
 *                                        last = today), added/removed this week, usedByEmails. May 404 on an
 *                                        older deploy: softFail gives null and the cards fall back to a.total.
 *   GET /v1/audience/stats/{range}?$top  visits (an explicit $top is required; the bare call returns no items)
 *   GET /v1/sales/audiences/{range}      orders and sales (lists overlap, so these never sum)
 *
 * Lists render as a card grid with filter pills, a name search and the range picker. Built-in lists
 * (All Contacts, Customers, Inactive, Suppressed) sit on one quiet line below the grid.
 *
 * Compare: the "Compare lists" header button turns on a pick mode; with 2 to 4 picked, the Compare card shows
 * range bars, the day-by-day line (POST /v1/audience/trends, see trend-chart.jsx for the window guard) and the
 * overlap pairs (POST /v1/audience/overlap-detail) with "Create list of A not B" (POST /v1/audience/from-difference).
 *
 * Audience ids exceed 2^53: they arrive as strings or JSONbig values and are OPAQUE. String(id) for keys, URLs
 * and joins; never Number() them.
 */

const RANGES = [["today", "Today"], ["yesterday", "Yesterday"], ["this_week", "This Week"], ["last_week", "Last Week"], ["this_month", "This Month"], ["last_month", "Last Month"], ["this_quarter", "This Quarter"], ["last_quarter", "Last Quarter"], ["this_year", "This Year"], ["last_year", "Last Year"], ["all_time", "All Time"]];
const SYSTEM_NAMES = ["All Contacts", "Customers", "Inactive", "Suppressed"];
const SYSTEM_LABELS = {
  "All Contacts": "Everyone",
  Customers: "Customers",
  Inactive: "Inactive",
  Suppressed: "Can't email"
};

/** Kind of a list: static / dynamic / mirror / system (falls back to the legacy name check). */
const kindOf = a => (a.kind || (SYSTEM_NAMES.includes(a.name) ? "system" : "static")).toLowerCase();
const isSystemList = a => kindOf(a) === "system" || SYSTEM_NAMES.includes(a.name);

/** §7.1 kind pill: static / dynamic / mirror / system (falls back to the legacy audienceType / name). */
export function kindPill(a) {
  const kind = kindOf(a);
  return /*#__PURE__*/React.createElement("span", {
    className: `wa-pill kind-${kind}`,
    title: kind === "dynamic" ? "members computed from a rule" : kind === "mirror" ? "a provider list Plunk keeps in sync" : kind === "system" ? "platform-managed" : "members you add"
  }, kind);
}
export function fetchAudienceApi(api, type = "audience") {
  const Service = ServiceFactory.getType(api, type);
  return new Service().getAsync();
}
export function mmss(v) {
  const s = Math.round(Number(v) || 0);
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}
export function statusChip(status) {
  const map = {
    Active: "ok",
    Processing: "build",
    Inactive: "muted",
    Error: "flag"
  };
  return /*#__PURE__*/React.createElement("span", {
    className: `aud-chip ${map[status] || "muted"}`
  }, status);
}
const PROVIDERS = {
  mailchimp: "Mailchimp",
  constant_contact: "Constant Contact",
  constantcontact: "Constant Contact",
  smartlead: "Smartlead",
  meta: "Meta",
  facebook: "Meta",
  google: "Google",
  googleads: "Google Ads",
  shopify: "Shopify",
  klaviyo: "Klaviyo"
};
const providerName = p => {
  if (!p) return "another app";
  const k = String(p).toLowerCase().replace(/[\s-]+/g, "_");
  return PROVIDERS[k] || PROVIDERS[k.replace(/_/g, "")] || String(p);
};
const plural = (n, one, many) => `${num(n)} ${Number(n) === 1 ? one : many}`;
const wholeUsd = v => `$${Math.round(Number(v) || 0).toLocaleString("en-US")}`;
const createdOf = a => a.createdDate || a.createDate || a.created_date || a.created || null;

/** First non-null point to the last point fell by more than 5%. */
function isShrinking(trend) {
  if (!Array.isArray(trend) || trend.length < 2) return false;
  const pts = trend.filter(v => v !== null && v !== undefined).map(Number);
  if (pts.length < 2) return false;
  const first = pts[0],
    last = pts[pts.length - 1];
  return first > 0 && last < first * 0.95;
}

/** How a list gets its people, in plain words. */
function fillsLine(a) {
  if (a.status === "Processing") return "Updating…";
  const kind = kindOf(a);
  const desc = a.description ? String(a.description).trim() : "";
  if (kind === "dynamic") return desc ? `Fills itself: ${desc}` : "Fills itself";
  if (kind === "mirror") return `Kept in step with ${providerName(a.provider)}`;
  return desc ? `People you add: ${desc}` : "People you add";
}

/** 8-bar member trend: bars scaled to the series max, nulls as faint stubs, last bar dark. */
function MiniTrend({
  trend
}) {
  if (!Array.isArray(trend) || trend.length === 0) return null;
  const vals = trend.map(v => v === null || v === undefined ? null : Number(v) || 0);
  const max = Math.max(1, ...vals.filter(v => v !== null));
  const known = vals.filter(v => v !== null);
  const label = known.length >= 2 ? `Members over ${vals.length} weeks: from ${num(known[0])} to ${num(known[known.length - 1])}` : `Members over ${vals.length} weeks`;
  return /*#__PURE__*/React.createElement("div", {
    className: "al-trend",
    role: "img",
    "aria-label": label
  }, vals.map((v, i) => /*#__PURE__*/React.createElement("span", {
    key: i,
    className: `al-bar${v === null ? " stub" : ""}${i === vals.length - 1 ? " now" : ""}`,
    style: v === null ? undefined : {
      height: `${Math.max(2, Math.round(v / max * 30))}px`
    }
  })));
}

/** Small "···" menu on a list card (Open, Remove). */
function CardMenu({
  name,
  onOpen,
  onRemove
}) {
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef(null);
  React.useEffect(() => {
    if (!open) return undefined;
    const onDoc = e => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const onKey = e => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);
  return /*#__PURE__*/React.createElement("span", {
    className: "al-menu",
    ref: ref
  }, /*#__PURE__*/React.createElement("button", {
    type: "button",
    className: "al-menu-btn",
    "aria-label": `More for ${name}`,
    "aria-haspopup": "menu",
    "aria-expanded": open,
    onClick: () => setOpen(!open)
  }, "\xB7\xB7\xB7"), open ? /*#__PURE__*/React.createElement("div", {
    className: "al-menu-pop",
    role: "menu"
  }, /*#__PURE__*/React.createElement("button", {
    type: "button",
    role: "menuitem",
    onClick: () => {
      setOpen(false);
      onOpen();
    }
  }, "Open"), /*#__PURE__*/React.createElement("button", {
    type: "button",
    role: "menuitem",
    className: "danger",
    onClick: () => {
      setOpen(false);
      onRemove();
    }
  }, "Remove")) : null);
}
const FILTERS = [["all", "All"], ["dynamic", "Fills itself"], ["static", "People you add"], ["attention", "Needs attention"]];
class AudiencesViewComponent extends React.Component {
  constructor(props) {
    super(props);
    _defineProperty(this, "onRange", v => {
      if (v) setGlobalRange(v);
    });
    _defineProperty(this, "onCreated", saved => {
      this.setState({
        newOpen: false
      });
      toast(`List “${saved && saved.name || "created"}” created.`);
      if (saved && saved.id !== undefined) this.props.history.push(`/Audiences/Detail/${encodeURIComponent(String(saved.id))}/${this.range()}`);else this.load();
    });
    _defineProperty(this, "detailPath", a => `/Audiences/Detail/${encodeURIComponent(String(a.id))}/${this.range()}`);
    _defineProperty(this, "openDetail", a => {
      this.props.history.push(this.detailPath(a));
    });
    _defineProperty(this, "togglePicking", () => {
      if (this.state.picking) {
        this._compareTok++;
        this.setState({
          picking: false,
          selected: {},
          compareTrend: undefined,
          overlap: undefined,
          diff: null,
          diffErr: ""
        });
      } else {
        this.setState({
          picking: true
        });
      }
    });
    _defineProperty(this, "toggleSelect", a => {
      this.setState(s => {
        const selected = {
          ...s.selected
        };
        const k = String(a.id);
        if (selected[k]) delete selected[k];else if (Object.keys(selected).length < 4) selected[k] = true;
        return {
          selected,
          diff: null,
          diffErr: ""
        };
      }, this.refreshCompare);
    });
    /** Re-fetch the day-by-day line and the overlap for the current 2 to 4 picks (stale replies are dropped). */
    _defineProperty(this, "refreshCompare", () => {
      const tok = ++this._compareTok;
      const picked = (this.state.data || []).filter(({
        a
      }) => this.state.selected[String(a.id)]);
      if (picked.length < 2) {
        this.setState({
          compareTrend: undefined,
          overlap: undefined
        });
        return;
      }
      this.setState({
        compareTrend: undefined,
        overlap: undefined
      });
      const live = () => this._alive && tok === this._compareTok;
      if (rangeWindow(this.range())) {
        fetchTrends(this.range(), picked.map(({
          a
        }) => a.name)).then(t => {
          if (live()) this.setState({
            compareTrend: t
          });
        }).catch(() => {
          if (live()) this.setState({
            compareTrend: null
          });
        });
      }
      overlapDetail(picked.map(({
        a
      }) => String(a.id))).then(o => {
        if (live()) this.setState({
          overlap: o && Array.isArray(o.pairs) ? o : null
        });
      }).catch(() => {
        if (live()) this.setState({
          overlap: null
        });
      });
    });
    _defineProperty(this, "createDifference", async () => {
      const {
        diff
      } = this.state;
      if (!diff) return;
      if (!diff.name.trim()) {
        this.setState({
          diffErr: "A name is required."
        });
        return;
      }
      this.setState({
        diffBusy: true,
        diffErr: ""
      });
      try {
        const r = await fromDifference({
          name: diff.name.trim(),
          includeId: String(diff.includeId),
          excludeId: String(diff.excludeId)
        });
        toast(`List “${r && r.name || diff.name}” created${r && r.total != null ? ` with ${num(r.total)} members` : ""}.`);
        this.setState({
          diff: null,
          diffBusy: false
        });
        if (r && r.id !== undefined) this.props.history.push(`/Audiences/Detail/${encodeURIComponent(String(r.id))}/${this.range()}`);else this.load();
      } catch (e) {
        this.setState({
          diffBusy: false,
          diffErr: e.status === 409 ? "A list with that name already exists." : e.message || "Create failed"
        });
      }
    });
    _defineProperty(this, "deleteAudience", async a => {
      this.setState({
        deleting: String(a.id)
      });
      try {
        const api = "/audience";
        const Service = ServiceFactory.getType(api, "audience");
        const res = await new Service().deleteAsync(String(a.id));
        const removed = res && res.membersRemoved !== undefined ? Number(res.membersRemoved) : null;
        toast(removed !== null ? `“${a.name}” removed. Its ${removed.toLocaleString("en-US")} members stay in your contacts.` : `“${a.name}” removed.`);
        this.setState(s => {
          const selected = {
            ...s.selected
          };
          delete selected[String(a.id)];
          return {
            confirmDelete: null,
            deleting: null,
            selected
          };
        });
        this.load();
      } catch (err) {
        this.setState({
          deleting: null
        });
        toast(`Could not remove “${a.name}”: ${err.message || "error"}`);
      }
    });
    this.state = {
      data: null,
      error: false,
      cards: undefined,
      // undefined loading · null unavailable · {String(id): card}
      filter: "all",
      q: "",
      newOpen: /[?&]new=1/.test(window.location.search),
      picking: false,
      // "Compare lists" pick mode
      selected: {},
      // String(id) -> true (compare picks)
      compareMetric: "sessions",
      compareTrend: undefined,
      overlap: undefined,
      // undefined loading · null unavailable · {pairs, all}
      diff: null,
      // {includeId, excludeId, name} inline create form
      diffBusy: false,
      diffErr: "",
      confirmDelete: null,
      // String(id) of the card showing "Yes, remove / Keep"
      deleting: null
    };
    this._alive = true;
    this._compareTok = 0;
  }
  range() {
    const r = this.props.params && this.props.params.range;
    return r && (RANGES.some(x => x[0] === r) || /^custom_\d{8}_\d{8}$/.test(r)) ? r : getGlobalRange();
  }
  componentDidMount() {
    this.load();
    this._periodTok = subscribePeriod(({
      range
    }) => {
      if (!this._alive || !range) return;
      const target = `/Audiences/${range}`;
      if (window.location.pathname === target) {
        this.load();
      } else {
        this.props.history.push(target);
      }
    });
  }
  componentWillUnmount() {
    this._alive = false;
    unsubscribePeriod(this._periodTok);
  }
  componentDidUpdate(prev) {
    if ((prev.params && prev.params.range || "") !== (this.props.params && this.props.params.range || "")) {
      this.setState({
        data: null,
        error: false
      });
      this.load();
    }
  }
  async load() {
    const range = this.range();
    getListCards().then(res => {
      if (!this._alive) return;
      const items = res && Array.isArray(res.items) ? res.items : null;
      if (!items) {
        this.setState({
          cards: null
        });
        return;
      }
      const by = {};
      items.forEach(c => {
        by[String(c.id)] = c;
      });
      this.setState({
        cards: by
      });
    }).catch(() => {
      if (this._alive) this.setState({
        cards: null
      });
    });
    try {
      const [audiences, statsRes, sales] = await Promise.all([fetchAudienceApi("/audience"), fetchAudienceApi(`/audience/stats/${range}?$top=500`, "product").catch(() => null), fetchAudienceApi(`/sales/audiences/${range}`, "product").catch(() => [])]);
      if (!this._alive) return;
      const stats = {};
      (statsRes && statsRes.items || (Array.isArray(statsRes) ? statsRes : [])).forEach(s => {
        stats[String(s.id)] = s;
        stats[`n:${s.name}`] = s;
      });
      const salesBy = {};
      (sales || []).forEach(s => {
        salesBy[String(s.audienceId)] = s;
      });
      const rows = (Array.isArray(audiences) ? audiences : audiences.items || []).map(a => {
        const st = stats[String(a.id)] || stats[`n:${a.name}`] || null;
        const sl = salesBy[String(a.id)] || null;
        return {
          a,
          st,
          sl
        };
      });
      this.setState({
        data: rows
      }, () => {
        if (this.state.picking) this.refreshCompare();
      });
    } catch (e) {
      if (this._alive) this.setState({
        error: true
      });
    }
  }
  renderOverlap(selectedRows) {
    const {
      overlap,
      diff,
      diffBusy,
      diffErr
    } = this.state;
    const nameOf = id => {
      const r = selectedRows.find(x => String(x.a.id) === String(id));
      return r ? r.a.name : String(id);
    };
    if (overlap === undefined) return /*#__PURE__*/React.createElement("div", {
      className: "aud-overlap"
    }, /*#__PURE__*/React.createElement("h4", {
      className: "aud-h4"
    }, "Overlap"), /*#__PURE__*/React.createElement(Pending, {
      h: 60
    }));
    if (overlap === null) return /*#__PURE__*/React.createElement("div", {
      className: "aud-overlap"
    }, /*#__PURE__*/React.createElement("h4", {
      className: "aud-h4"
    }, "Overlap"), /*#__PURE__*/React.createElement(Empty, null, "Overlap between lists is not available yet on this workspace."));
    const pairs = overlap.pairs || [];
    return /*#__PURE__*/React.createElement("div", {
      className: "aud-overlap"
    }, /*#__PURE__*/React.createElement("h4", {
      className: "aud-h4"
    }, "Overlap", overlap.all != null ? /*#__PURE__*/React.createElement("span", {
      className: "cnt"
    }, num(overlap.all), " in all ", selectedRows.length) : null), /*#__PURE__*/React.createElement("div", {
      className: "tblwrap"
    }, /*#__PURE__*/React.createElement("table", {
      className: "territory-table wa-table aud-table"
    }, /*#__PURE__*/React.createElement("thead", null, /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("th", null, "A"), /*#__PURE__*/React.createElement("th", null, "B"), /*#__PURE__*/React.createElement("th", {
      className: "n"
    }, "In both"), /*#__PURE__*/React.createElement("th", {
      className: "n"
    }, "Only A"), /*#__PURE__*/React.createElement("th", {
      className: "n"
    }, "Only B"), /*#__PURE__*/React.createElement("th", {
      className: "aud-actions-col"
    }))), /*#__PURE__*/React.createElement("tbody", null, pairs.map(p => {
      const k = `${p.a}|${p.b}`;
      return /*#__PURE__*/React.createElement("tr", {
        key: k
      }, /*#__PURE__*/React.createElement("td", null, /*#__PURE__*/React.createElement("span", {
        className: "name"
      }, nameOf(p.a))), /*#__PURE__*/React.createElement("td", null, /*#__PURE__*/React.createElement("span", {
        className: "name"
      }, nameOf(p.b))), /*#__PURE__*/React.createElement("td", {
        className: "n"
      }, num(p.both)), /*#__PURE__*/React.createElement("td", {
        className: "n"
      }, num(p.onlyA)), /*#__PURE__*/React.createElement("td", {
        className: "n"
      }, num(p.onlyB)), /*#__PURE__*/React.createElement("td", {
        className: "aud-actions"
      }, /*#__PURE__*/React.createElement("button", {
        className: "territory-toggle",
        onClick: () => this.setState({
          diff: {
            includeId: p.a,
            excludeId: p.b,
            name: `${nameOf(p.a)} not ${nameOf(p.b)}`
          },
          diffErr: ""
        })
      }, "Create list of A not B"), /*#__PURE__*/React.createElement("button", {
        className: "territory-toggle",
        onClick: () => this.setState({
          diff: {
            includeId: p.b,
            excludeId: p.a,
            name: `${nameOf(p.b)} not ${nameOf(p.a)}`
          },
          diffErr: ""
        })
      }, "B not A")));
    })))), diff ? /*#__PURE__*/React.createElement("div", {
      className: "aud-subform"
    }, /*#__PURE__*/React.createElement("div", {
      className: "ec-field"
    }, /*#__PURE__*/React.createElement("label", null, "New list: people in ", nameOf(diff.includeId), " who are not in ", nameOf(diff.excludeId)), /*#__PURE__*/React.createElement("div", {
      className: "ec-inline"
    }, /*#__PURE__*/React.createElement("input", {
      className: "ec-input aud-name-input",
      autoFocus: true,
      value: diff.name,
      onChange: e => this.setState({
        diff: {
          ...diff,
          name: e.target.value
        }
      })
    }), /*#__PURE__*/React.createElement(Button, {
      themeColor: "primary",
      disabled: diffBusy,
      onClick: this.createDifference
    }, diffBusy ? "Creating…" : "Create list"), /*#__PURE__*/React.createElement(Button, {
      disabled: diffBusy,
      onClick: () => this.setState({
        diff: null,
        diffErr: ""
      })
    }, "Cancel"))), /*#__PURE__*/React.createElement(ErrorLine, null, diffErr)) : null);
  }
  renderCompare(selectedRows) {
    const {
      compareMetric,
      compareTrend
    } = this.state;
    const METRICS = [["sessions", "Visits", r => r.st ? Number(r.st.sessions) || 0 : 0, num], ["uniqueUsers", "Visitors", r => r.st ? Number(r.st.uniqueUsers) || 0 : 0, num], ["conversions", "Conversions", r => r.st ? Number(r.st.conversions) || 0 : 0, num], ["sales", "Sales", r => r.sl ? Number(r.sl.totalAmount) || 0 : 0, usd]];
    const metric = METRICS.find(([k]) => k === compareMetric) || METRICS[0];
    const maxMetric = Math.max(...selectedRows.map(r => metric[2](r)), 1);
    return /*#__PURE__*/React.createElement(Card, {
      title: "Compare",
      hint: `${metric[1]} · bars are totals for the period; the day-by-day line appears when the period has daily data`,
      right: /*#__PURE__*/React.createElement("span", {
        className: "seg"
      }, METRICS.map(([k, l]) => /*#__PURE__*/React.createElement("button", {
        key: k,
        className: compareMetric === k ? "on" : "",
        onClick: () => this.setState({
          compareMetric: k
        })
      }, l)))
    }, Array.isArray(compareTrend) && compareTrend.length > 0 && compareMetric !== "sales" && /*#__PURE__*/React.createElement(AudienceTrendChart, {
      rows: compareTrend,
      height: 180,
      series: selectedRows.map(r => ({
        name: r.a.name,
        metric: compareMetric,
        label: r.a.name
      }))
    }), /*#__PURE__*/React.createElement("div", {
      className: "aud-compare"
    }, selectedRows.map(r => /*#__PURE__*/React.createElement("div", {
      key: String(r.a.id),
      className: "aud-compare-row"
    }, /*#__PURE__*/React.createElement("span", {
      className: "aud-compare-name"
    }, r.a.name), /*#__PURE__*/React.createElement("div", {
      className: "aud-compare-track"
    }, /*#__PURE__*/React.createElement("div", {
      className: "aud-compare-bar",
      style: {
        width: `${Math.max(2, metric[2](r) / maxMetric * 100)}%`
      }
    })), /*#__PURE__*/React.createElement("span", {
      className: "aud-compare-val"
    }, metric[3](metric[2](r)))))), this.renderOverlap(selectedRows));
  }

  /** Everything a card shows, derived once per row. */
  cardModel({
    a,
    st,
    sl
  }) {
    const {
      cards
    } = this.state;
    const c = cards ? cards[String(a.id)] || null : null;
    const members = c && c.members != null ? Number(c.members) || 0 : Number(a.total) || 0;
    const withEmail = c && c.withEmail != null && members > 0 ? Number(c.withEmail) || 0 : null;
    const trend = c && Array.isArray(c.trend) ? c.trend : null;
    const shrinking = isShrinking(trend);
    const errored = a.status === "Error";
    const kind = kindOf(a);
    const created = createdOf(a) || c && c.createdDate || null;
    const recent = created ? Date.now() - new Date(created).getTime() < 30 * 86400000 : false;
    const needsFirstEmail = !!c && Number(c.usedByEmails) === 0 && kind === "static" && recent;
    const visits = st ? Number(st.sessions) || 0 : 0;
    const orders = sl ? Number(sl.orders) || 0 : 0;
    const sales = sl ? Number(sl.totalAmount) || 0 : 0;
    return {
      a,
      kind,
      members,
      withEmail,
      trend,
      shrinking,
      errored,
      needsFirstEmail,
      visits,
      orders,
      sales,
      usedBy: c ? Number(c.usedByEmails) || 0 : 0,
      attention: shrinking || errored
    };
  }
  renderCard(m) {
    const {
      picking,
      selected,
      confirmDelete,
      deleting
    } = this.state;
    const {
      a
    } = m;
    const id = String(a.id);
    const on = !!selected[id];
    const full = picking && !on && Object.keys(selected).length >= 4;
    const stats = [m.visits > 0 ? plural(m.visits, "visit", "visits") : null, m.orders > 0 ? plural(m.orders, "order", "orders") : null, m.sales > 0 ? `${wholeUsd(m.sales)} in sales` : null].filter(Boolean);
    const tag = m.errored ? /*#__PURE__*/React.createElement("span", {
      className: "al-tag warn"
    }, "Could not update") : m.shrinking ? /*#__PURE__*/React.createElement("span", {
      className: "al-tag warn"
    }, "Shrinking") : m.needsFirstEmail ? /*#__PURE__*/React.createElement("span", {
      className: "al-tag info"
    }, "Needs a first email") : null;
    const pct = m.withEmail !== null ? Math.round(m.withEmail / m.members * 100) : null;
    return /*#__PURE__*/React.createElement("div", {
      key: id,
      className: `al-card${picking ? " picking" : ""}${on ? " on" : ""}`
    }, picking ? /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "al-card-hit",
      "aria-pressed": on,
      disabled: full,
      title: full ? "You can compare up to 4 lists" : undefined,
      "aria-label": `${on ? "Remove" : "Add"} ${a.name} ${on ? "from" : "to"} the comparison`,
      onClick: () => this.toggleSelect(a)
    }) : /*#__PURE__*/React.createElement(Link, {
      className: "al-card-hit",
      to: this.detailPath(a),
      "aria-label": `Open ${a.name}`
    }), /*#__PURE__*/React.createElement("div", {
      className: "al-card-head"
    }, picking ? /*#__PURE__*/React.createElement("span", {
      className: `al-check${on ? " on" : ""}`,
      "aria-hidden": "true"
    }, on ? "✓" : "") : null, /*#__PURE__*/React.createElement("b", {
      className: "al-name",
      title: a.name
    }, a.name), tag, !picking ? /*#__PURE__*/React.createElement(CardMenu, {
      name: a.name,
      onOpen: () => this.openDetail(a),
      onRemove: () => this.setState({
        confirmDelete: id
      })
    }) : null), /*#__PURE__*/React.createElement("span", {
      className: "al-fills",
      title: fillsLine(a)
    }, fillsLine(a)), /*#__PURE__*/React.createElement("div", {
      className: "al-mid"
    }, /*#__PURE__*/React.createElement("div", {
      className: "al-count"
    }, /*#__PURE__*/React.createElement("b", null, num(m.members)), /*#__PURE__*/React.createElement("span", null, m.members === 1 ? "person" : "people", pct !== null ? ` · ${pct}% can get email` : "")), /*#__PURE__*/React.createElement(MiniTrend, {
      trend: m.trend
    })), /*#__PURE__*/React.createElement("div", {
      className: "al-foot"
    }, confirmDelete === id ? /*#__PURE__*/React.createElement("span", {
      className: "al-confirm"
    }, /*#__PURE__*/React.createElement(InlineConfirm, {
      variant: "row",
      question: "Remove this list? Its people stay in your contacts.",
      yes: "Yes, remove",
      busy: deleting === id,
      onYes: () => this.deleteAudience(a),
      onNo: () => this.setState({
        confirmDelete: null
      })
    })) : /*#__PURE__*/React.createElement(React.Fragment, null, stats.length ? stats.map(s => /*#__PURE__*/React.createElement("span", {
      key: s
    }, s)) : /*#__PURE__*/React.createElement("span", null, "No activity in this period"), m.usedBy > 0 ? /*#__PURE__*/React.createElement("span", {
      className: "al-used"
    }, "Used by ", plural(m.usedBy, "email", "emails")) : null)));
  }
  render() {
    const {
      data,
      error,
      filter,
      q,
      picking,
      selected,
      newOpen
    } = this.state;
    const range = this.range();
    const pickCount = Object.keys(selected).length;
    const shell = body => /*#__PURE__*/React.createElement(AudiencesShell, {
      subtitle: SUBTITLES.lists,
      cls: "al-view",
      onNew: () => this.setState({
        newOpen: true
      }),
      actions: /*#__PURE__*/React.createElement(Button, {
        onClick: this.togglePicking
      }, picking ? "Done comparing" : "Compare lists")
    }, newOpen ? /*#__PURE__*/React.createElement(ListEditor, {
      audience: null,
      lists: (data || []).map(({
        a
      }) => a),
      onClose: () => this.setState({
        newOpen: false
      }),
      onSaved: this.onCreated
    }) : null, body);
    if (error) return /*#__PURE__*/React.createElement(Error, null);
    if (!data) return shell(/*#__PURE__*/React.createElement(Loader, null));
    const system = data.filter(({
      a
    }) => isSystemList(a)).sort((x, y) => SYSTEM_NAMES.indexOf(x.a.name) - SYSTEM_NAMES.indexOf(y.a.name));
    const models = data.filter(({
      a
    }) => !isSystemList(a)).map(r => this.cardModel(r)).sort((x, y) => y.members - x.members);
    const counts = {
      all: models.length,
      dynamic: models.filter(m => m.kind === "dynamic").length,
      static: models.filter(m => m.kind === "static").length,
      attention: models.filter(m => m.attention).length
    };
    const needle = q.trim().toLowerCase();
    const shown = models.filter(m => filter === "all" || (filter === "attention" ? m.attention : m.kind === filter)).filter(m => !needle || String(m.a.name || "").toLowerCase().includes(needle));
    const selectedRows = data.filter(({
      a
    }) => selected[String(a.id)]);
    return shell(/*#__PURE__*/React.createElement(React.Fragment, null, models.length === 0 ? /*#__PURE__*/React.createElement("div", {
      className: "al-empty"
    }, /*#__PURE__*/React.createElement("b", null, "No lists yet"), /*#__PURE__*/React.createElement("span", null, "A list is a group of people you can send email to, show ads to or study. Start one from people you pick or from a rule that fills it for you."), /*#__PURE__*/React.createElement(Button, {
      themeColor: "primary",
      onClick: () => this.setState({
        newOpen: true
      })
    }, "New list")) : /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
      className: "al-bar-row"
    }, /*#__PURE__*/React.createElement("div", {
      className: "al-pills",
      role: "group",
      "aria-label": "Show lists"
    }, FILTERS.map(([k, l]) => /*#__PURE__*/React.createElement("button", {
      key: k,
      type: "button",
      className: `al-pill${filter === k ? " on" : ""}`,
      "aria-pressed": filter === k,
      onClick: () => this.setState({
        filter: k
      })
    }, l, " ", num(counts[k])))), /*#__PURE__*/React.createElement("span", {
      className: "al-grow"
    }), /*#__PURE__*/React.createElement("label", {
      className: "al-search"
    }, /*#__PURE__*/React.createElement("span", {
      className: "material-icons al-search-ico",
      "aria-hidden": "true"
    }, "search"), /*#__PURE__*/React.createElement("input", {
      type: "search",
      placeholder: "Search lists",
      "aria-label": "Search lists",
      value: q,
      onChange: e => this.setState({
        q: e.target.value
      })
    }))), picking ? /*#__PURE__*/React.createElement("div", {
      className: "al-pickline",
      role: "status"
    }, pickCount < 2 ? `Pick 2 to 4 lists to compare. ${pickCount} picked.` : `${pickCount} lists picked${pickCount >= 4 ? " (that is the most you can compare)" : ""}.`) : null, picking && selectedRows.length >= 2 ? this.renderCompare(selectedRows) : null, shown.length ? /*#__PURE__*/React.createElement("div", {
      className: "al-grid"
    }, shown.map(m => this.renderCard(m))) : /*#__PURE__*/React.createElement("div", {
      className: "al-none"
    }, "No lists match. Try another filter or clear the search.")), system.length ? /*#__PURE__*/React.createElement("div", {
      className: "al-builtin"
    }, /*#__PURE__*/React.createElement("b", null, "Built-in lists"), /*#__PURE__*/React.createElement("span", {
      className: "al-builtin-items"
    }, system.map(({
      a
    }, i) => /*#__PURE__*/React.createElement(React.Fragment, {
      key: String(a.id)
    }, i > 0 ? /*#__PURE__*/React.createElement("span", {
      className: "al-dot",
      "aria-hidden": "true"
    }, "\xB7") : null, /*#__PURE__*/React.createElement(Link, {
      to: this.detailPath(a),
      title: a.name === "Customers" ? "Everyone linked to an account, including people at accounts that are still leads or prospects. The Contacts tab's Customers count only people at accounts in the customer stage." : undefined
    }, SYSTEM_LABELS[a.name] || a.name, " ", num(this.cardModel({
      a
    }).members))))), /*#__PURE__*/React.createElement("span", {
      className: "al-grow"
    }), /*#__PURE__*/React.createElement("span", {
      className: "al-builtin-note"
    }, "Kept up to date for you")) : null));
  }
}
export default withRouter(AudiencesViewComponent);